import { useCallback, useEffect, useRef, useState } from "react";
import { FileDown, FileSpreadsheet, FileText, Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FormError, PageHeader } from "@/components/common/ui-bits";
import { PeriodSelector } from "@/components/dashboard/PeriodSelector";
import { SummaryCards } from "@/components/dashboard/SummaryCards";
import { ReportCategoryChart, ReportIncomeChart } from "@/components/reports/ReportCharts";
import { ComparisonTable, MonthlyTable, PnlTable, VendorTable } from "@/components/reports/ReportTables";
import { api, errMsg } from "@/lib/api";
import { downloadReport } from "@/lib/download";
import { formatDate } from "@/lib/format";

const FORMATS = [
  { fmt: "pdf", label: "Download PDF", icon: FileDown },
  { fmt: "xlsx", label: "Download Excel", icon: FileSpreadsheet },
  { fmt: "csv", label: "Download CSV", icon: FileText },
];

function DownloadButtons({ period }) {
  const [busy, setBusy] = useState(null);
  const run = async (fmt) => {
    setBusy(fmt);
    await downloadReport(fmt, period.start, period.end);
    setBusy(null);
  };
  return FORMATS.map(({ fmt, label, icon: Icon }) => (
    <Button key={fmt} variant={fmt === "pdf" ? "default" : "outline"} disabled={!period || !!busy} onClick={() => run(fmt)}
      className="rounded-xl h-10" data-testid={`report-download-${fmt}`}>
      {busy === fmt ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Icon className="h-4 w-4 mr-1.5" />}{label}
    </Button>
  ));
}

export default function ReportsPage() {
  const [period, setPeriod] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const reqId = useRef(0);

  const load = useCallback(async (p) => {
    const id = ++reqId.current;
    try {
      const { data: d } = await api.get("/reports", { params: { start: p.start, end: p.end } });
      if (id === reqId.current) { setData(d); setError(""); }
    } catch (e) {
      if (id === reqId.current) setError(errMsg(e));
    }
  }, []);

  useEffect(() => { if (period) load(period); }, [period, load]);
  const shown = data?.period;

  return (
    <div>
      <PageHeader eyebrow="Profit & Loss, comparisons and downloads" title="Reports" testId="reports-title">
        <DownloadButtons period={shown} />
      </PageHeader>
      <div className="surface p-3 sm:p-4 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 fade-up">
        <PeriodSelector onChange={setPeriod} />
        {shown && <div className="text-xs text-muted-foreground" data-testid="report-period-label">{shown.label} · {formatDate(shown.start)} – {formatDate(shown.end)}</div>}
      </div>
      {error && <div className="mb-4"><FormError message={error} testId="report-error" /></div>}
      {!data ? <div className="text-sm text-muted-foreground" data-testid="report-loading">Loading…</div> : (
        <div className="space-y-6">
          {!data.has_data && (
            <div className="flex items-center gap-2 rounded-2xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground" data-testid="report-no-data">
              <Info className="h-4 w-4" /> No data for this period
            </div>
          )}
          <SummaryCards totals={data.totals} />
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            <div className="xl:col-span-7 min-w-0"><ReportIncomeChart rows={data.monthly} period={data.period} empty={!data.has_data} /></div>
            <div className="xl:col-span-5 min-w-0"><ReportCategoryChart lines={data.pnl.expense} period={data.period} /></div>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
            <PnlTable pnl={data.pnl} />
            <MonthlyTable rows={data.monthly} totals={data.monthly_totals} />
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
            <ComparisonTable lines={data.comparisons} which="previous" base={data.previous_period} />
            <ComparisonTable lines={data.comparisons} which="last_year" base={data.last_year_period} />
          </div>
          <VendorTable rows={data.vendors} totals={data.vendor_totals} />
        </div>
      )}
    </div>
  );
}
